import { Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SpaceProvider } from "@/hooks/use-space";
import { SavedItemsProvider } from "@/hooks/use-saved-items";
import { ManageListsProvider } from "@/hooks/use-manage-lists";
import { BrandOpportunitiesProvider } from "@/hooks/use-brand-opportunities";
import { EngageCampaignsProvider } from "@/hooks/use-engage-campaigns";
import { AuthProvider } from "@/hooks/use-auth";
import CommunityAccessGate from "@/components/auth/CommunityAccessGate";
import { ContentSkeleton } from "@/components/layout/AppShellSkeleton";
import DiscoverSkeleton from "@/components/discover/DiscoverSkeleton";
import SavedSkeleton from "@/components/saved/SavedSkeleton";
import CookieBanner from "@/components/CookieBanner";
import EngageSkeleton from "@/components/engage/EngageSkeleton";
import CampaignDetailSkeleton from "@/components/engage/CampaignDetailSkeleton";
import BrandProfileSkeleton from "@/components/brand/BrandProfileSkeleton";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ScrollToTop from "./components/ScrollToTop";
import { useIsMobile } from "@/hooks/use-mobile";
import { isKitheDomain } from "@/lib/kithe-domain";
import { lazyWithRetry } from "@/lib/lazy-with-retry";

// Eager: landing/auth entry points users hit first
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";

// Lazy: split heavier routes into separate chunks to improve Speed Index
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Discover = lazyWithRetry(() => import("./pages/Discover"));
const CommunityProfile = lazyWithRetry(() => import("./pages/CommunityProfile"));
const EngagePage = lazyWithRetry(() => import("./pages/EngagePage"));
const EngageCampaignDetail = lazyWithRetry(() => import("./pages/EngageCampaignDetail"));
const ActivateFamilyPage = lazyWithRetry(() => import("./pages/ActivateFamilyPage"));
const ActivateActivationPage = lazyWithRetry(() => import("./pages/ActivateActivationPage"));
const ActivationResults = lazyWithRetry(() => import("./pages/ActivationResults"));
const ActivationDetail = lazyWithRetry(() => import("./pages/ActivationDetail"));
const AccountPage = lazyWithRetry(() => import("./pages/AccountPage"));
const BrandProfilePage = lazyWithRetry(() => import("./pages/BrandProfilePage"));
const SignupPage = lazyWithRetry(() => import("./pages/SignupPage"));
const PlanSelector = lazyWithRetry(() => import("./components/auth/PlanSelector"));
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const CheckEmailPage = lazyWithRetry(() => import("./pages/CheckEmailPage"));
const WelcomePage = lazyWithRetry(() => import("./pages/WelcomePage"));
const AuthCallbackPage = lazyWithRetry(() => import("./pages/AuthCallbackPage"));

const SavedCommunitiesPage = lazyWithRetry(() => import("./pages/SavedCommunitiesPage"));
const InterestListDetail = lazyWithRetry(() => import("./pages/InterestListDetail"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const OnboardingPage = lazyWithRetry(() => import("./pages/OnboardingPage"));
const BuildingProfilePage = lazyWithRetry(() => import("./pages/BuildingProfilePage"));
const BlogIndex = lazyWithRetry(() => import("./pages/BlogIndex"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const TeamPage = lazyWithRetry(() => import("./pages/TeamPage"));
const WhyErnestPage = lazyWithRetry(() => import("./pages/WhyErnestPage"));
const TheMapPage = lazyWithRetry(() => import("./pages/TheMapPage"));
const CommunityMarketingReport2026 = lazyWithRetry(() => import("./pages/CommunityMarketingReport2026"));
const ForCommunitiesPage = lazyWithRetry(() => import("./pages/ForCommunitiesPage"));
const GuideSponsorsRunClub = lazyWithRetry(() => import("./pages/GuideSponsorsRunClub"));
const GuideCommunitySponsorship = lazyWithRetry(() => import("./pages/GuideCommunitySponsorship"));
const GuideSponsorshipDossier = lazyWithRetry(() => import("./pages/GuideSponsorshipDossier"));
const GuideClubValue = lazyWithRetry(() => import("./pages/GuideClubValue"));
const GuideProductSeeding = lazyWithRetry(() => import("./pages/GuideProductSeeding"));
const GuideEventFunding = lazyWithRetry(() => import("./pages/GuideEventFunding"));
const GuideMemberPerks = lazyWithRetry(() => import("./pages/GuideMemberPerks"));
const GuideClubSponsorshipTiers = lazyWithRetry(() => import("./pages/GuideClubSponsorshipTiers"));
const GuidesIndex = lazyWithRetry(() => import("./pages/GuidesIndex"));
const ClubTypePage = lazyWithRetry(() => import("./pages/ClubTypePage"));
const BrandSectorsHub = lazyWithRetry(() => import("./pages/BrandSectorsHub"));
const BrandSectorPage = lazyWithRetry(() => import("./pages/BrandSectorPage"));
const CommunitySignupPage = lazyWithRetry(() => import("./pages/CommunitySignupPage"));
const KitheLoginPage = lazyWithRetry(() => import("./pages/KitheLoginPage"));
const KitheForgotPasswordPage = lazyWithRetry(() => import("./pages/KitheForgotPasswordPage"));
const KitheResetPasswordPage = lazyWithRetry(() => import("./pages/KitheResetPasswordPage"));
const CommunityDashboardPage = lazyWithRetry(() => import("./pages/CommunityDashboardPage"));
const CommunityProtectedRoute = lazyWithRetry(() => import("./components/auth/CommunityProtectedRoute"));
const AdminCommunitySubmissionsPage = lazyWithRetry(() => import("./pages/AdminCommunitySubmissionsPage"));
const UnsubscribePage = lazyWithRetry(() => import("./pages/UnsubscribePage"));
const AdminProtectedRoute = lazyWithRetry(() => import("./components/auth/AdminProtectedRoute"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 min – avoid refetching on every mount
      gcTime: 10 * 60 * 1000,         // 10 min – keep cache longer
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** What shows while a page's code loads: the shape of Discover and Saved on their routes, a generic page elsewhere. */
const RouteFallback = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/discover")) return <DiscoverSkeleton />;
  if (pathname === "/saved") return <SavedSkeleton />;
  if (pathname === "/engage") return <EngageSkeleton />;
  if (pathname.startsWith("/engage/campaign/")) return <CampaignDetailSkeleton />;
  if (pathname === "/brand") return <BrandProfileSkeleton />;
  return <ContentSkeleton />;
};

/** Sends a GA4 page_view on every client-side navigation */
const GaPageView = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof (window as any).gtag === "function") {
      (window as any).gtag("config", "G-GL723DLG47", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  return null;
};

/** Toasts default to bottom-right, which on mobile in the community-signup
 *  wizard sits right on top of the sticky Continue/Back bar. Top-center there
 *  only, on mobile only — every other route/desktop keeps the default. */
const SonnerToaster = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const position = isMobile && location.pathname === "/community-signup" ? "top-center" : "bottom-right";
  return <Sonner position={position} />;
};

  const App = () => {
  useEffect(() => {
    // index.html ships ernest's icons (see the comment there — it's the shared
    // static HTML that non-JS crawlers read). On kithe.club, repoint every icon
    // reference at Kithe's set so tabs, PWA installs and share sheets are right.
    if (isKitheDomain()) {
      const setHref = (selector: string, href: string) => {
        const el = document.querySelector<HTMLLinkElement>(selector);
        if (el) el.href = href;
      };
      setHref('link[rel="icon"]', "/kithe-favicon.png");
      setHref('link[rel="apple-touch-icon"]', "/kithe-icon-180.png");
      setHref('link[rel="manifest"]', "/kithe-manifest.json");

      const appTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
      if (appTitle) appTitle.content = "Kithe";
    }
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <AuthProvider>
        <BrowserRouter>
          <GaPageView />
          <SonnerToaster />
          <CookieBanner />
          <ScrollToTop />
          <Suspense fallback={null}>
            <Routes>
              {/* Public marketing routes — "/" serves the community landing page on kithe.club */}
              <Route path="/" element={isKitheDomain() ? <ForCommunitiesPage /> : <HomePage />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/why-ernest" element={<WhyErnestPage />} />
              <Route path="/the-map" element={<TheMapPage />} />
              <Route path="/community-marketing-report-2026" element={<CommunityMarketingReport2026 />} />
              {/* One guide, two indexable language URLs (hreflang pair). */}
              <Route path="/guide/trouver-des-sponsors-run-club" element={<GuideSponsorsRunClub lang="fr" />} />
              <Route path="/guide/how-to-find-sponsors-run-club" element={<GuideSponsorsRunClub lang="en" />} />
              <Route path="/guide/trouver-des-sponsors-communaute" element={<GuideCommunitySponsorship lang="fr" />} />
              <Route path="/guide/how-to-find-sponsors-community" element={<GuideCommunitySponsorship lang="en" />} />
              <Route path="/guide/dossier-de-sponsoring" element={<GuideSponsorshipDossier lang="fr" />} />
              <Route path="/guide/how-to-write-a-sponsorship-proposal" element={<GuideSponsorshipDossier lang="en" />} />
              <Route path="/guide/pourquoi-les-marques-sponsorisent-les-clubs" element={<GuideClubValue lang="fr" />} />
              <Route path="/guide/why-brands-sponsor-clubs" element={<GuideClubValue lang="en" />} />
              <Route path="/guide/seeding-produit-club" element={<GuideProductSeeding lang="fr" />} />
              <Route path="/guide/product-seeding-for-clubs" element={<GuideProductSeeding lang="en" />} />
              <Route path="/guide/faire-financer-son-evenement" element={<GuideEventFunding lang="fr" />} />
              <Route path="/guide/get-your-event-funded" element={<GuideEventFunding lang="en" />} />
              <Route path="/guide/trouver-des-sponsors-book-club-supper-club" element={<GuideClubSponsorshipTiers lang="fr" />} />
              <Route path="/guide/how-to-find-sponsors-book-club-supper-club" element={<GuideClubSponsorshipTiers lang="en" />} />
              <Route path="/guide/avantages-membres-club" element={<GuideMemberPerks lang="fr" />} />
              <Route path="/guide/member-perks-from-brands" element={<GuideMemberPerks lang="en" />} />
              {/* One page per club type, one URL per language. */}
              <Route path="/for/:slug" element={<ClubTypePage lang="en" />} />
              <Route path="/pour/:slug" element={<ClubTypePage lang="fr" />} />
              <Route path="/guides" element={<GuidesIndex lang="fr" />} />
              <Route path="/guides/en" element={<GuidesIndex lang="en" />} />
              <Route path="/industries" element={<BrandSectorsHub />} />
              <Route path="/industries/:industry" element={<BrandSectorPage />} />

              {/* Public auth routes — shared paths branch by domain (kithe.club gets the community auth pages) */}
              <Route path="/login" element={isKitheDomain() ? <KitheLoginPage /> : <LoginPage />} />
              <Route path="/signup" element={isKitheDomain() ? <CommunitySignupPage /> : <PlanSelector />} />
              <Route path="/signup-account" element={<Navigate to="/signup" replace />} />
              <Route path="/forgot-password" element={isKitheDomain() ? <KitheForgotPasswordPage /> : <ForgotPasswordPage />} />
              <Route path="/reset-password" element={isKitheDomain() ? <KitheResetPasswordPage /> : <ResetPasswordPage />} />
              <Route path="/check-email" element={<CheckEmailPage />} />
              {/* Reached from an email footer, token in the query string. */}
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Community-side (marketplace) routes — kept working under their original prefixed
                  paths on both domains, so existing links/bookmarks never break. */}
              <Route path="/kithe-for-communities" element={<ForCommunitiesPage />} />
              {/* Shorter, brand-neutral alias for sharing — same page as above. */}
              <Route path="/homepage" element={<ForCommunitiesPage />} />
              <Route path="/community-signup" element={<CommunitySignupPage />} />
              <Route path="/kithe-login" element={<KitheLoginPage />} />
              <Route path="/kithe-forgot-password" element={<KitheForgotPasswordPage />} />
              <Route path="/kithe-reset-password" element={<KitheResetPasswordPage />} />
              <Route
                path="/my-community"
                element={
                  <CommunityProtectedRoute>
                    <CommunityDashboardPage />
                  </CommunityProtectedRoute>
                }
              />
              <Route
                path="/admin/community-submissions"
                element={
                  <AdminProtectedRoute>
                    <AdminCommunitySubmissionsPage />
                  </AdminProtectedRoute>
                }
              />

              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
              <Route path="/building-profile" element={<ProtectedRoute><BuildingProfilePage /></ProtectedRoute>} />


              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <SpaceProvider>
                      <SavedItemsProvider>
                        <ManageListsProvider>
                          <BrandOpportunitiesProvider>
                            <EngageCampaignsProvider>
                              <DashboardLayout>
                                <Suspense fallback={<RouteFallback />}>
                                  <Routes>
                                    <Route path="/" element={<Navigate to="/discover" replace />} />
                                    <Route path="/discover" element={<CommunityAccessGate loadingFallback={<DiscoverSkeleton />}><Discover /></CommunityAccessGate>} />
                                    <Route path="/saved" element={<CommunityAccessGate loadingFallback={<SavedSkeleton />}><SavedCommunitiesPage /></CommunityAccessGate>} />
                                    <Route path="/saved/lists/:id" element={<CommunityAccessGate><InterestListDetail /></CommunityAccessGate>} />
                                    <Route path="/community/:id" element={<CommunityAccessGate><CommunityProfile /></CommunityAccessGate>} />
                                    <Route path="/activate/:family" element={<ActivateFamilyPage />} />
                                    <Route path="/activate/:family/:id" element={<ActivateActivationPage />} />
                                    <Route path="/engage" element={<EngagePage />} />
                                    <Route path="/engage/campaign/:id" element={<EngageCampaignDetail />} />
                                    <Route path="/engage/activation/:id" element={<ActivationResults />} />
                                    <Route path="/engage/activation/:id/detail" element={<ActivationDetail />} />
                                    <Route path="/account" element={<AccountPage />} />
                                    <Route path="/brand" element={<BrandProfilePage />} />
                                    <Route path="*" element={<NotFound />} />
                                  </Routes>
                                </Suspense>
                              </DashboardLayout>
                            </EngageCampaignsProvider>
                          </BrandOpportunitiesProvider>
                        </ManageListsProvider>
                      </SavedItemsProvider>
                    </SpaceProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
